import type { File as EvidenceUploadFile } from 'src/components/moleculesLegacy/FileUpload';
import { UploadScanStatus } from 'src/types';
import { guessPrimaryMimeTypeForAllowedFilename } from 'src/utils/validateFileUpload/fileValidation';

export interface DynamicQuestionUploadedFile {
  fileName: string;
  s3Path: string;
}

export type QaAnswerAttachmentRef = {
  id?: string;
  fileName?: string;
  fileKey?: string;
  fileSize?: number;
  mimeType?: string;
  fileExtension?: string;
};

/**
 * Portal evidence URLs use the short `s3path` segment; QA `fileKey` is the full S3 key.
 */
export function evidenceS3SourceFromQaFileKey(
  fileKey: string | undefined | null,
  reportId: string,
): string {
  if (fileKey == null || fileKey === '' || reportId === '') {
    return '';
  }
  if (fileKey === reportId) {
    return '';
  }
  const needle = `/${reportId}/`;
  const index = fileKey.indexOf(needle);
  if (index !== -1) {
    return fileKey.slice(index + needle.length);
  }
  const segments = fileKey.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? '';
}

export type BuildEvidenceUploadFilesFromPersistedUploadsResult = {
  valid: EvidenceUploadFile[];
  invalid: DynamicQuestionUploadedFile[];
};

const FILEPOND_PROCESSING_COMPLETE = 5;

function isValidPersistedDynamicQuestionUpload(
  f: DynamicQuestionUploadedFile,
): boolean {
  return f.s3Path.trim() !== '' && f.fileName.trim() !== '';
}

export function buildEvidenceUploadFilesFromPersistedUploads(
  files: DynamicQuestionUploadedFile[],
): BuildEvidenceUploadFilesFromPersistedUploadsResult {
  const valid: EvidenceUploadFile[] = [];
  const invalid: DynamicQuestionUploadedFile[] = [];
  const timestamp = new Date().toISOString();
  for (const f of files) {
    if (!isValidPersistedDynamicQuestionUpload(f)) {
      invalid.push(f);
      continue;
    }
    valid.push({
      source: f.s3Path.trim(),
      options: {
        type: 'local' as const,
        file: {
          name: f.fileName.trim(),
          size: 0,
          type: guessPrimaryMimeTypeForAllowedFilename(f.fileName.trim()),
        },
        metadata: { scanStatus: UploadScanStatus.PENDING },
      },
      status: FILEPOND_PROCESSING_COMPLETE,
      created: timestamp,
      updated: timestamp,
    });
  }
  return { valid, invalid };
}

function isUploadedFileRecord(item: unknown): item is Record<string, unknown> {
  return typeof item === 'object' && item !== null;
}

export function parseDynamicQuestionUploadedFilesFromAnswerText(
  text: string | null | undefined,
): DynamicQuestionUploadedFile[] {
  if (text == null || text.trim() === '') {
    return [];
  }
  const trimmed = text.trim();
  if (!trimmed.startsWith('[')) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const out: DynamicQuestionUploadedFile[] = [];
    for (const item of parsed) {
      if (!isUploadedFileRecord(item)) continue;
      const fileName = item.fileName;
      const s3Path = item.s3Path;
      if (
        typeof fileName === 'string' &&
        fileName.trim() !== '' &&
        typeof s3Path === 'string' &&
        s3Path.trim() !== ''
      ) {
        out.push({ fileName: fileName.trim(), s3Path: s3Path.trim() });
      }
    }
    return out;
  } catch {
    return [];
  }
}

export function parseUploadedFilesFromAnswerValue(
  value: unknown,
): DynamicQuestionUploadedFile[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    return parseDynamicQuestionUploadedFilesFromAnswerText(value);
  }
  if (typeof value === 'object' && value !== null && 'text' in value) {
    const t = (value as { text?: unknown }).text;
    if (typeof t === 'string') {
      return parseDynamicQuestionUploadedFilesFromAnswerText(t);
    }
  }
  return [];
}

export function stringifyDynamicQuestionUploadedFiles(
  files: DynamicQuestionUploadedFile[],
): string {
  return JSON.stringify(files);
}

export function buildUploadedFilesFromEvidenceState(
  evidenceUploadFiles: Array<{
    source: string;
    options: { file: { name: string } };
  }>,
): DynamicQuestionUploadedFile[] {
  return evidenceUploadFiles
    .filter((f) => f.source.trim() !== '')
    .map((f) => ({
      fileName: f.options.file.name,
      s3Path: f.source,
    }));
}
